import * as THREE from "three";
import { mat } from "./WorldModels";
import type { DogPose } from "./ParkCritter";

const TRIGGER_R = 5.2;
const SPRAY_DUR = 2.85;
const COOLDOWN = 1.35;
/** Riser top - taller than park humans (~1.5-1.7). */
const PIPE_H = 2.85;

/**
 * Motion-sensing park sprayer: a tall green irrigation pipe that bursts a
 * water fan when dogs / people walk past (local ambient gag; reactions are networked).
 */
export class ParkLawnSprayer {
  readonly group = new THREE.Group();
  private readonly tileX: number;
  private readonly tileZ: number;
  private readonly spray: THREE.Points;
  private readonly sprayPos: Float32Array;
  private readonly sprayBase: Float32Array;
  private readonly spray2: THREE.Points;
  private readonly spray2Pos: Float32Array;
  private readonly spray2Base: Float32Array;
  private readonly mist: THREE.Mesh;
  private readonly mist2: THREE.Mesh;
  private readonly nozzle: THREE.Mesh;
  private readonly lens: THREE.Mesh;
  private active = false;
  private sprayT = 0;
  private coolT = 0;
  private readonly facing: number;
  private readonly dropletCount: number;
  private readonly dropletCount2: number;

  constructor(
    midX: number,
    midZ: number,
    groundY: (x: number, z: number) => number,
    variant = 0,
  ) {
    this.tileX = midX - 0.5;
    this.tileZ = midZ - 0.5;
    this.facing = (variant % 8) * (Math.PI / 4) + (variant % 2) * 0.2;
    this.dropletCount = 96 + (variant % 5) * 12;
    this.dropletCount2 = 48 + (variant % 3) * 8;

    const gy = groundY(midX - 0.5, midZ - 0.5);
    this.group.position.set(midX, gy, midZ);
    this.group.rotation.y = this.facing;

    const green = mat(0x43a047);
    const greenDark = mat(0x2e7d32);
    const greenLight = mat(0x66bb6a);
    const metal = mat(0x546e7a);

    const footing = new THREE.Mesh(
      new THREE.CylinderGeometry(0.38, 0.44, 0.14, 12),
      mat(0x9e9e9e),
    );
    // Bottom clears grass so the concrete pad doesn't shimmer.
    footing.position.y = 0.09;

    const riser = new THREE.Mesh(
      new THREE.CylinderGeometry(0.14, 0.16, PIPE_H, 12),
      green,
    );
    riser.position.y = PIPE_H * 0.5;

    for (const t of [0.35, 0.55, 0.75, 0.92] as const) {
      const ring = new THREE.Mesh(
        new THREE.CylinderGeometry(0.175, 0.175, 0.07, 12),
        greenDark,
      );
      ring.position.y = PIPE_H * t;
      this.group.add(ring);
    }

    const joint = new THREE.Mesh(new THREE.SphereGeometry(0.2, 10, 8), greenLight);
    joint.position.y = PIPE_H;

    const boom = new THREE.Mesh(
      new THREE.CylinderGeometry(0.1, 0.11, 0.85, 10),
      green,
    );
    boom.rotation.z = Math.PI / 2;
    boom.position.set(0.35, PIPE_H, 0.05);

    const boomCap = new THREE.Mesh(
      new THREE.CylinderGeometry(0.13, 0.13, 0.1, 10),
      greenDark,
    );
    boomCap.rotation.z = Math.PI / 2;
    boomCap.position.set(0.78, PIPE_H, 0.05);

    const sensorBox = new THREE.Mesh(
      new THREE.BoxGeometry(0.28, 0.22, 0.32),
      metal,
    );
    sensorBox.position.set(0, PIPE_H - 0.55, 0.22);

    this.lens = new THREE.Mesh(
      new THREE.SphereGeometry(0.07, 10, 8),
      new THREE.MeshBasicMaterial({ color: 0xff7043 }),
    );
    this.lens.position.set(0, PIPE_H - 0.55, 0.4);

    this.nozzle = new THREE.Mesh(
      new THREE.CylinderGeometry(0.06, 0.12, 0.28, 10),
      metal,
    );
    this.nozzle.rotation.x = Math.PI / 2.35;
    this.nozzle.position.set(0.92, PIPE_H - 0.02, 0.18);

    const valve = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.18, 0.32), greenDark);
    valve.position.set(-0.35, 0.12, 0.15);

    this.sprayPos = new Float32Array(this.dropletCount * 3);
    this.sprayBase = new Float32Array(this.dropletCount * 3);
    for (let i = 0; i < this.dropletCount; i++) {
      const a = ((i / this.dropletCount) * 1.25 - 0.62) * Math.PI;
      const reach = 1.8 + (i % 8) * 0.55;
      const lift = 0.45 + (i % 7) * 0.42;
      this.sprayBase[i * 3] = Math.sin(a) * reach * 0.85;
      this.sprayBase[i * 3 + 1] = lift;
      this.sprayBase[i * 3 + 2] = 0.4 + Math.cos(a * 0.35) * reach;
      this.sprayPos[i * 3] = this.sprayBase[i * 3]!;
      this.sprayPos[i * 3 + 1] = this.sprayBase[i * 3 + 1]!;
      this.sprayPos[i * 3 + 2] = this.sprayBase[i * 3 + 2]!;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(this.sprayPos, 3));
    this.spray = new THREE.Points(
      geo,
      new THREE.PointsMaterial({
        color: 0xd8f4ff,
        size: 0.18,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        sizeAttenuation: true,
      }),
    );
    this.spray.position.set(0.95, PIPE_H - 0.15, 0.25);
    this.spray.visible = false;

    this.spray2Pos = new Float32Array(this.dropletCount2 * 3);
    this.spray2Base = new Float32Array(this.dropletCount2 * 3);
    for (let i = 0; i < this.dropletCount2; i++) {
      const a = ((i / this.dropletCount2) * 1.4 - 0.7) * Math.PI;
      const reach = 2.6 + (i % 5) * 0.7;
      const lift = 0.2 + (i % 6) * 0.28;
      this.spray2Base[i * 3] = Math.sin(a) * reach * 1.05;
      this.spray2Base[i * 3 + 1] = lift;
      this.spray2Base[i * 3 + 2] = 0.8 + Math.cos(a * 0.25) * reach;
      this.spray2Pos[i * 3] = this.spray2Base[i * 3]!;
      this.spray2Pos[i * 3 + 1] = this.spray2Base[i * 3 + 1]!;
      this.spray2Pos[i * 3 + 2] = this.spray2Base[i * 3 + 2]!;
    }
    const geo2 = new THREE.BufferGeometry();
    geo2.setAttribute("position", new THREE.BufferAttribute(this.spray2Pos, 3));
    this.spray2 = new THREE.Points(
      geo2,
      new THREE.PointsMaterial({
        color: 0xb3e5fc,
        size: 0.11,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        sizeAttenuation: true,
      }),
    );
    this.spray2.position.set(0.95, PIPE_H - 0.2, 0.25);
    this.spray2.visible = false;

    this.mist = new THREE.Mesh(
      new THREE.CircleGeometry(2.8, 24),
      new THREE.MeshBasicMaterial({
        color: 0xa8d8f0,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    this.mist.rotation.x = -Math.PI / 2;
    this.mist.position.set(0.5, 0.05, 2.6);

    this.mist2 = new THREE.Mesh(
      new THREE.CircleGeometry(1.6, 18),
      new THREE.MeshBasicMaterial({
        color: 0xc8ebf8,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    this.mist2.rotation.x = -Math.PI / 2;
    this.mist2.position.set(0.2, 0.06, 1.4);

    this.group.add(
      footing,
      riser,
      joint,
      boom,
      boomCap,
      sensorBox,
      this.lens,
      this.nozzle,
      valve,
      this.spray,
      this.spray2,
      this.mist,
      this.mist2,
    );
  }

  /** True if a pose is inside the motion-sensor radius. */
  isNear(x: number, z: number): boolean {
    const dx = x - this.tileX;
    const dz = z - this.tileZ;
    return dx * dx + dz * dz <= TRIGGER_R * TRIGGER_R;
  }

  /** True while the spray fan is active and a pose is in range. */
  isSoaking(x: number, z: number): boolean {
    return this.active && this.isNear(x, z);
  }

  /** Returns true on the frame a spray burst starts. */
  update(dtSec: number, movers: readonly DogPose[]): boolean {
    const dt = Math.min(0.05, Math.max(0.001, dtSec));
    if (this.coolT > 0) {
      this.coolT = Math.max(0, this.coolT - dt);
    }

    const near = movers.some((m) => this.isNear(m.x, m.y));
    let justTriggered = false;

    if (!this.active && this.coolT <= 0 && near) {
      this.active = true;
      this.sprayT = 0;
      this.spray.visible = true;
      this.spray2.visible = true;
      justTriggered = true;
    }

    if (this.active) {
      this.sprayT += dt;
      const t = this.sprayT / SPRAY_DUR;
      const burst = t < 0.1 ? t / 0.1 : t > 0.68 ? Math.max(0, 1 - (t - 0.68) / 0.32) : 1;
      this.animateSpray(this.sprayT, burst);
      if (this.sprayT >= SPRAY_DUR) {
        this.active = false;
        this.coolT = COOLDOWN;
        this.spray.visible = false;
        this.spray2.visible = false;
        this.setSprayOpacity(0, 0);
        if (this.mist.material instanceof THREE.MeshBasicMaterial) {
          this.mist.material.opacity = 0;
        }
        if (this.mist2.material instanceof THREE.MeshBasicMaterial) {
          this.mist2.material.opacity = 0;
        }
      }
    } else if (near && this.coolT <= 0.35) {
      this.pulseSensor(0.45 + Math.sin(performance.now() * 0.012) * 0.25);
    } else {
      this.pulseSensor(this.coolT > 0 ? 0.15 : 0.55);
    }

    return justTriggered;
  }

  destroy(): void {
    for (const pts of [this.spray, this.spray2]) {
      pts.geometry.dispose();
      const sm = pts.material;
      if (sm instanceof THREE.Material) {
        sm.dispose();
      }
    }
    for (const mesh of [this.mist, this.mist2]) {
      const mm = mesh.material;
      if (mm instanceof THREE.Material) {
        mm.dispose();
      }
    }
    const lm = this.lens.material;
    if (lm instanceof THREE.Material) {
      lm.dispose();
    }
    this.group.removeFromParent();
  }

  private setSprayOpacity(a: number, b: number): void {
    const m1 = this.spray.material;
    if (m1 instanceof THREE.PointsMaterial) {
      m1.opacity = a;
    }
    const m2 = this.spray2.material;
    if (m2 instanceof THREE.PointsMaterial) {
      m2.opacity = b;
    }
  }

  private animateSpray(age: number, strength: number): void {
    const m1 = this.spray.material;
    if (m1 instanceof THREE.PointsMaterial) {
      m1.opacity = 0.35 + strength * 0.65;
      m1.size = 0.12 + strength * 0.14;
    }
    const m2 = this.spray2.material;
    if (m2 instanceof THREE.PointsMaterial) {
      m2.opacity = 0.25 + strength * 0.55;
      m2.size = 0.08 + strength * 0.1;
    }
    if (this.mist.material instanceof THREE.MeshBasicMaterial) {
      this.mist.material.opacity = strength * 0.42;
    }
    if (this.mist2.material instanceof THREE.MeshBasicMaterial) {
      this.mist2.material.opacity = strength * 0.28;
    }
    this.nozzle.rotation.x = Math.PI / 2.35 + Math.sin(age * 20) * 0.1 * strength;
    this.pulseSensor(0.85 + Math.sin(age * 22) * 0.15);

    for (let i = 0; i < this.dropletCount; i++) {
      const phase = age * (5.2 + (i % 5) * 0.4) + i * 0.37;
      const flutter = Math.sin(phase) * 0.22 * strength;
      const fall = (age * 0.55 + (i % 9) * 0.06) % 1.35;
      this.sprayPos[i * 3] = this.sprayBase[i * 3]! + flutter;
      this.sprayPos[i * 3 + 1] = Math.max(0.06, this.sprayBase[i * 3 + 1]! * strength - fall * 0.85);
      this.sprayPos[i * 3 + 2] = this.sprayBase[i * 3 + 2]! + Math.cos(phase * 0.7) * 0.14;
    }
    this.spray.geometry.attributes.position!.needsUpdate = true;

    for (let i = 0; i < this.dropletCount2; i++) {
      const phase = age * (6.1 + (i % 4) * 0.5) + i * 0.51;
      const flutter = Math.cos(phase) * 0.28 * strength;
      const fall = (age * 0.7 + (i % 6) * 0.08) % 1.5;
      this.spray2Pos[i * 3] = this.spray2Base[i * 3]! + flutter;
      this.spray2Pos[i * 3 + 1] = Math.max(0.04, this.spray2Base[i * 3 + 1]! * strength - fall * 1.05);
      this.spray2Pos[i * 3 + 2] = this.spray2Base[i * 3 + 2]! + Math.sin(phase * 0.6) * 0.18;
    }
    this.spray2.geometry.attributes.position!.needsUpdate = true;
  }

  private pulseSensor(amount: number): void {
    const mat = this.lens.material;
    if (mat instanceof THREE.MeshBasicMaterial) {
      mat.color.setRGB(1, 0.35 + amount * 0.35, 0.15);
    }
  }
}
